import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  animate,
  AnimationEvent,
  query,
  stagger,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { TranslocoPipe } from '@jsverse/transloco';

const SLIDE_COUNT = 4;
const SLIDE_DURATION_MS = 8000;
const TICK_MS = 32;

@Component({
  selector: 'app-auth-hero-carousel',
  templateUrl: './auth-hero-carousel.component.html',
  styleUrl: './auth-hero-carousel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe],
  animations: [
    trigger('heroSlide', [
      transition(':enter', [
        query(
          '.carousel-line',
          [
            style({ opacity: 0, transform: 'translateY(1.25rem)' }),
            stagger(95, [
              animate(
                '480ms cubic-bezier(0.16, 1, 0.3, 1)',
                style({ opacity: 1, transform: 'none' })
              ),
            ]),
          ],
          { optional: true }
        ),
      ]),
      transition(':leave', [
        animate(
          '260ms ease',
          style({ opacity: 0, transform: 'translateY(-0.5rem)' })
        ),
      ]),
    ]),
  ],
})
export class AuthHeroCarouselComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly slideCount = SLIDE_COUNT;
  readonly slideDurationMs = SLIDE_DURATION_MS;

  readonly activeIndex = signal(0);
  /** When false, current slide plays :leave; next slide mounts only after it finishes. */
  readonly slideInView = signal(true);
  readonly progress = signal(0);
  readonly reduceMotion = signal(false);

  readonly slideIndices = computed(() =>
    Array.from({ length: SLIDE_COUNT }, (_, i) => i)
  );

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private pendingIndex = 0;
  private awaitingLeave = false;

  constructor() {
    afterNextRender(() => {
      this.reduceMotion.set(
        typeof window !== 'undefined' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
      if (!this.reduceMotion()) {
        this.startProgressTimer();
      }
    });
    this.destroyRef.onDestroy(() => this.clearTimer());
  }

  textKey(index: number, field: 'title' | 'lead' | 'detail'): string {
    return `company-tool.views.authentication.hero.carousel.slide${index + 1}.${field}`;
  }

  fillScale(i: number): number {
    const active = this.activeIndex();
    const p = this.progress();
    if (i < active) {
      return 1;
    }
    if (i > active) {
      return 0;
    }
    return p;
  }

  onSlideAnimDone(event: AnimationEvent): void {
    if (event.phaseName !== 'done' || !this.awaitingLeave) {
      return;
    }
    // :leave completes with * → void
    if (event.toState !== 'void') {
      return;
    }
    this.awaitingLeave = false;
    this.activeIndex.set(this.pendingIndex);
    this.slideInView.set(true);
    this.progress.set(0);
    if (!this.reduceMotion()) {
      this.startProgressTimer();
    }
  }

  goTo(index: number): void {
    if (index < 0 || index >= SLIDE_COUNT) {
      return;
    }
    this.beginSlideTransition(index);
  }

  private advance(): void {
    const next = (this.activeIndex() + 1) % SLIDE_COUNT;
    this.beginSlideTransition(next);
  }

  private beginSlideTransition(target: number): void {
    if (this.reduceMotion()) {
      this.awaitingLeave = false;
      this.slideInView.set(true);
      this.activeIndex.set(target);
      this.progress.set(0);
      this.clearTimer();
      this.startProgressTimer();
      return;
    }

    if (target === this.activeIndex() && this.slideInView()) {
      return;
    }

    this.clearTimer();

    if (this.awaitingLeave) {
      this.pendingIndex = target;
      return;
    }

    this.pendingIndex = target;
    this.awaitingLeave = true;
    this.slideInView.set(false);
  }

  private startProgressTimer(): void {
    this.clearTimer();
    if (this.reduceMotion()) {
      return;
    }
    const start = Date.now();
    this.intervalId = setInterval(() => {
      const elapsed = Date.now() - start;
      const p = Math.min(1, elapsed / SLIDE_DURATION_MS);
      this.progress.set(p);
      if (elapsed >= SLIDE_DURATION_MS) {
        this.clearTimer();
        this.advance();
      }
    }, TICK_MS);
  }

  private clearTimer(): void {
    if (this.intervalId != null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
